import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';
import { useWebSocket } from '../contexts/WebSocketContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const Tracks = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [tracks, setTracks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, trackId: null });
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    department: '',
    academicYear: ''
  });

  useEffect(() => {
    fetchDepartments();
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    if (selectedDepartment && selectedAcademicYear) {
      fetchTracks();
    }
  }, [selectedDepartment, selectedAcademicYear]);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'connection') {
      console.log('Connection message:', data.message);
    } else if (data.type === 'track_updated') {
      console.log('🔄 Track update reçu:', data.data?.action);
      if (selectedDepartment) {
        fetchTracks();
      }
    }
  }, [selectedDepartment]);

  // Use WebSocket context
  const { isConnected } = useWebSocket('tracks', handleWebSocketMessage);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/departments?limit=1000`);
      setDepartments(Array.isArray(response.data) ? response.data : response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des départements:', error);
      setLoading(false);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/academic-years?limit=1000`);
      const years = Array.isArray(response.data) ? response.data : response.data.data || [];
      setAcademicYears(years);
      // Auto-select first academic year if available
      if (years.length > 0 && !selectedAcademicYear) {
        setSelectedAcademicYear(years[0]._id);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des années académiques:', error);
    }
  };

  const fetchTracks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tracks?department=${selectedDepartment}&academicYear=${selectedAcademicYear}&limit=1000&_t=${Date.now()}`);
      setTracks(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des parcours:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (editingTrack) {
        await axios.put(`${API_URL}/api/tracks/${editingTrack._id}`, formData, config);
        showSuccess('Parcours modifié avec succès');
      } else {
        await axios.post(`${API_URL}/api/tracks`, formData, config);
        showSuccess('Parcours ajouté avec succès');
      }
      closeModal();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showError(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      await axios.delete(`${API_URL}/api/tracks/${id}`, config);
      showSuccess('Parcours supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showError('Erreur lors de la suppression');
    }
  };

  const confirmDelete = (trackId) => {
    setDeleteConfirm({ show: true, trackId });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.trackId) {
      await handleDelete(deleteConfirm.trackId);
    }
    setDeleteConfirm({ show: false, trackId: null });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, trackId: null });
  };

  const openModal = (track = null) => {
    setEditingTrack(track);
    setFormData({
      name: track?.name || '',
      code: track?.code || '',
      department: track?.department?._id || selectedDepartment,
      academicYear: track?.academicYear?._id || selectedAcademicYear
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTrack(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h1 className="text-xl font-semibold text-gray-900">Parcours</h1>
            <span className={`px-2 py-1 text-xs rounded-full ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? '🟢 Live' : '🔴 Manual'}
            </span>
          </div>
          <button 
            onClick={() => openModal()} 
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:ring-2 focus:ring-primary-500 ${
              selectedDepartment && selectedAcademicYear
                ? 'bg-primary-600 hover:bg-primary-700' 
                : 'bg-gray-400 cursor-not-allowed'
            }`}
            disabled={!selectedDepartment || !selectedAcademicYear}
          >
            + Ajouter
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
              <select 
                value={selectedDepartment} 
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner un département</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>{dept.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Année Académique</label>
              <select 
                value={selectedAcademicYear} 
                onChange={(e) => setSelectedAcademicYear(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner une année</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>{year.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {selectedDepartment && selectedAcademicYear ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {tracks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom du Parcours</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {tracks.map(track => (
                      <tr key={track._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{track.code}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{track.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{track.academicYear?.name || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex gap-1">
                            <button 
                              onClick={() => openModal(track)}
                              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                              title="Modifier"
                            >
                              ✏️
                            </button>
                            <button 
                              onClick={() => confirmDelete(track._id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Supprimer"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🛤️</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun parcours</h3>
                <p className="text-gray-600">Aucun parcours trouvé pour ce département et cette année.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Sélectionnez un département et une année</h3>
            <p className="text-gray-600">Veuillez sélectionner un département et une année académique pour voir les parcours.</p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingTrack ? 'Modifier Parcours' : 'Ajouter Parcours'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ex: DSI"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du Parcours</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ex: Développement des Systèmes d'Information"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année Académique</label>
                  <select
                    name="academicYear"
                    value={formData.academicYear}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Sélectionner une année</option>
                    {academicYears.map(year => (
                      <option key={year._id} value={year._id}>{year.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingTrack ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-red-600">🗑️ Confirmer la suppression</h3>
                <button onClick={cancelDelete} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 mb-4">Êtes-vous sûr de vouloir supprimer ce parcours ? Cette action est irréversible.</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tracks;